import React from 'react'
import PageExtras from '../components/shared/PageExtras'
import PageHeader from '../components/shared/PageHeader'
import type { PracticeArea } from '../types'
import AnimatedSection from '../components/ui/AnimatedSection'
import { Briefcase, Shield } from 'react-feather'
import PracticeAreaCard from '../components/practice-areas/PracticeAreaCard'
import CTABand from '../components/funnel/CTABand'
import Seo from '../components/shared/Seo'
import { generateItemListSchema, generateBreadcrumbSchema } from '../lib/schema'
import FlippableCard from '../components/ui/FlippableCard'
import { useData } from '../context/DataContext'
import { useTranslation, Trans } from 'react-i18next'

const PracticeAreasPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { practiceAreas: practiceAreasData, firmInfo } = useData();

  // CRITICAL FIX: Add a guard to prevent a crash if the data is unexpectedly not an array.
  // This ensures the component never throws a ".map is not a function" error, which would
  // cause the prerendering process to fail and serve a blank page.
  if (!practiceAreasData || !Array.isArray(practiceAreasData)) {
    // Return a fallback or null to prevent a crash.
    // In this case, letting the layout render without this section is the safest option.
    return null;
  }

  const itemListItems = practiceAreasData.map(area => ({ 
    name: area.title, 
    url: `${firmInfo.url}/practice-areas/${area.slug}`, 
    description: area.subtitle 
  }));
  const itemListSchema = generateItemListSchema(itemListItems, "Personal Injury Practice Areas");
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'Practice Areas', path: '/practice-areas' }
  ]);


  return (
    <>
      <Seo
        title={t('practiceAreasPage.seoTitle')}
        description={t('practiceAreasPage.seoDescription')}
        schema={[itemListSchema, breadcrumbSchema]}
      />
      <div>
        <PageHeader
          title={t('practiceAreasPage.pageTitle')}
          subtitle={t('practiceAreasPage.pageSubtitle')}
          imageUrl="https://images.pexels.com/photos/5668882/pexels-photo-5668882.jpeg?auto=compress&cs=tinysrgb&w=1600"
        />

        {/* New "Focused Experience" Section */}
        <div className="py-24 dark-section-bg">
          <div className="mx-auto max-w-7xl px-6">
            <AnimatedSection>
              <div className="grid lg:grid-cols-2 gap-16 items-start">
                <div>
                  <h2 className="text-4xl md:text-5xl font-serif font-bold text-balance">
                    <Trans
                      i18nKey="practiceAreasPage.focusedTitle"
                      t={t}
                      components={{ gold: <span className="gold-gradient-text" /> }}
                    />
                  </h2>
                  <p className="mt-6 text-lg text-white/80 leading-relaxed">
                    {t('practiceAreasPage.focusedParagraph')}
                  </p>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-6 rounded-xl bg-white/5 border border-white/10 glass-effect">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg gold-gradient-bg flex items-center justify-center text-black">
                        <Briefcase size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{t('practiceAreasPage.experienceTitle')}</h3>
                         <p className="text-white/80 text-sm mt-1">
                          {t('practiceAreasPage.experienceDescription')}
                        </p>
                      </div>
                  </div>
                  <div className="flex items-start gap-4 p-6 rounded-xl bg-white/5 border border-white/10 glass-effect">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg gold-gradient-bg flex items-center justify-center text-black">
                        <Shield size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{t('practiceAreasPage.strategyTitle')}</h3>
                        <p className="text-white/80 text-sm mt-1">
                          {t('practiceAreasPage.strategyDescription')}
                        </p>
                      </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>

        <div className="py-24">
          <div className="mx-auto max-w-7xl px-6">
            <AnimatedSection>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {practiceAreasData.map((area, index) => (
                  <PracticeAreaCard
                    key={area.slug}
                    area={area}
                    index={index}
                  />
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>

        <PageExtras pageKey="practiceAreas" />

        <CTABand
          headline={t('practiceAreasPage.ctaTitle')}
          subheadline={t('practiceAreasPage.ctaSubtitle')}
          buttonText={t('practiceAreasPage.ctaButton')}
          buttonLink="/contact"
        />
      </div>
    </>
  )
}

export default PracticeAreasPage